/**
 * API клієнт для doc.production_reports — Звіт виробництва за зміну.
 * Backend: /api/v1/production-reports. Permissions: production.report.create/post/delete.
 */
import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';

export interface ProductionReportProductIn {
  line_number: number;
  nomenclature_id: string;
  quantity: string;
  uom_id?: string | null;
  specification_id?: string | null;
  account_code?: string | null;        // рахунок обліку продукції (26)
  cost_account_code?: string | null;   // рахунок витрат (231)
  planned_cost?: string | null;        // планова собівартість од. (тільки 1C_WIP)
  planned_amount?: string | null;      // планова сума рядка (тільки 1C_WIP)
  nomenclature_group_id?: string | null;
}

export interface ProductionReportProduct extends ProductionReportProductIn {
  id: string;
  cost_per_unit: string | null;
  total_cost: string | null;
}

export interface ProductionReportMaterialIn {
  line_number: number;
  nomenclature_id: string;
  quantity: string;
  uom_id?: string | null;
  account_code?: string | null;        // рахунок обліку матеріалу (201)
}

export interface ProductionReportMaterial extends ProductionReportMaterialIn {
  id: string;
  cost_per_unit: string | null;
  total_cost: string | null;
}

export interface ProductionReportReturnableWasteIn {
  line_number: number;
  nomenclature_id: string;
  quantity: string;
  price?: string | null;
  amount: string;                      // вартість відходів (зменшує собівартість)
  account_code?: string | null;        // рахунок оприбуткування (201)
  nomenclature_group_id?: string | null;
}

export interface ProductionReportReturnableWaste extends ProductionReportReturnableWasteIn {
  id: string;
}

/** Рядок ТЧ «Послуги» (1С «Услуги», ч.649) — довідково, у проведенні участі не бере. */
export interface ProductionReportServiceIn {
  line_number: number;
  nomenclature_id: string;
  quantity: string;
  amount?: string | null;
  nomenclature_group_id?: string | null;
  comment?: string | null;
}

export interface ProductionReportServiceOut extends ProductionReportServiceIn {
  id: string;
}

export interface ProductionReportListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  warehouse_id: string;
  total_cost: string | null;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
  // Block 5.3c:
  state?: CancellableState;
}

export interface ProductionReport extends ProductionReportListItem {
  department_id: string | null;
  posted_at: string | null;
  comment: string | null;
  project_id: string | null;
  products: ProductionReportProduct[];
  materials: ProductionReportMaterial[];
  returnable_waste: ProductionReportReturnableWaste[];
  services: ProductionReportServiceOut[];
  // Block 5.3c:
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface ProductionReportCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  warehouse_id: string;
  department_id?: string | null;
  comment?: string | null;
  project_id?: string | null;
  products?: ProductionReportProductIn[];
  materials?: ProductionReportMaterialIn[];
  returnable_waste?: ProductionReportReturnableWasteIn[];
  services?: ProductionReportServiceIn[];
}

export type ProductionReportUpdate = Partial<Omit<ProductionReportCreate, 'organization_id'>>;

export const productionReports = {
  list: (params?: { organization_id?: string; date_from?: string; date_to?: string; sort_by?: string; sort_dir?: 'asc' | 'desc'; posted?: boolean; limit?: number; offset?: number }) =>
    api.get<Page<ProductionReportListItem>>('/production-reports', { params }).then((r) => r.data),
  get: (id: string) => api.get<ProductionReport>(`/production-reports/${id}`).then((r) => r.data),
  create: (payload: ProductionReportCreate) =>
    api.post<ProductionReport>('/production-reports', payload).then((r) => r.data),
  update: (id: string, payload: ProductionReportUpdate) =>
    api.patch<ProductionReport>(`/production-reports/${id}`, payload).then((r) => r.data),
  remove: (id: string) => api.delete(`/production-reports/${id}`).then(() => undefined),
  post: (id: string) => api.post<ProductionReport>(`/production-reports/${id}/post`).then((r) => r.data),
  unpost: (id: string) => api.post<ProductionReport>(`/production-reports/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/production-reports/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};
